import * as React from 'react'
import * as ReactDOM from 'react-dom'
import NextLink from 'next/link'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/next'
import * as consent from '@owl-nest/consent'
import * as date from '@owl-nest/date'
import * as duvet from '@ulule/duvet'
import * as hooks from '@owl-nest/hooks'
import { t, tc, tn, tnc } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as services from '@owl-nest/services'
import { CATEGORY, EVENT_TYPE, SITE, useTracking } from '@owl-nest/shadow'

import * as layout from '@ulule/layout'
import * as plume from '@ulule/owl-kit-components/next'

import { useDisplayMode } from '../../hooks/useDisplayMode'
import { getCurrentLocale } from '../../router'
import * as appUtils from '../../utils'
import { NavigateBackLink } from '../../components/NavigateBackLink'
import * as UFE from '../../UFE'

import * as newsStyle from '../style'

const RECENT_NEWS_COUNT = 6

type NewsDetailProps = {
  newsId: number
  project: models.project.Project
}

export function NewsPiece({ newsId, project }: NewsDetailProps): React.ReactElement {
  const auth = services.user.useAuth()
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const router = hooks.useNextRouter()
  const isClientSide = hooks.useClientSide()
  const tracking = useTracking()

  const isAuthPending = auth.type === 'initial'
  const hasUserNewsReadPermission = project.permissions.news?.includes(api.ProjectNewsPermission.READ) ?? false

  const { response: newsPieceResponse, query: queryNewsPiece } = services.news.useNewsPiece(newsId, {
    fetchOnMount: false,
    withToken: auth.type === 'loggedin',
  })

  React.useEffect(() => {
    if (!isAuthPending && newsPieceResponse.status === hooks.QueryStatus.PRISTINE) {
      queryNewsPiece()
    }
  }, [isAuthPending, newsPieceResponse.status])

  const useProjectNewsParams: services.news.UseProjectNewsParams = {
    limit: RECENT_NEWS_COUNT,
    sort: 'created_at',
  }

  if (hasUserNewsReadPermission) {
    useProjectNewsParams.status = 'online,waiting'
  } else {
    useProjectNewsParams.status = 'online'
  }

  const { response: listResponse, query: queryRecentNews } = services.news.useProjectNews(project.id, {
    fetchOnMount: false,
    params: useProjectNewsParams,
    withToken: auth.type === 'loggedin',
  })

  React.useEffect(() => {
    if (!isAuthPending && listResponse.status === hooks.QueryStatus.PRISTINE) {
      queryRecentNews()
    }
  }, [isAuthPending, listResponse.status])

  const currentLocale = getCurrentLocale(router)

  const setIsProjectDetailShareModalOpen = hooks.useSetModal('ProjectDetailShare')

  const modalContainer = React.useMemo(() => {
    if (router.isReady && typeof window !== 'undefined') {
      return document.getElementById('modal-container') ?? document.body
    }
  }, [router.isReady])

  if (newsPieceResponse.status === hooks.QueryStatus.FAILURE && typeof window !== 'undefined') {
    // FIXME: This will be eliminated by upcoming ISR
    window.location.href = '/handler404'
  }

  const newsPiece = newsPieceResponse.data

  const newsPieceContent =
    newsPiece && newsPiece.content && models.i18n.getWithLang(newsPiece.content, currentLocale, project.lang)
  const newsPiecePreview = newsPiece && models.i18n.getWithLang(newsPiece.preview, currentLocale, project.lang)
  const newsPieceTitle = newsPiece && models.i18n.getWithLang(newsPiece.title, currentLocale, project.lang)
  const newsPieceVideo = newsPiece?.video && models.i18n.getWithLang(newsPiece.video, currentLocale, project.lang)
  const newsPieceImage = newsPiece?.image && models.i18n.getWithLang(newsPiece.image, currentLocale, project.lang)

  // news.contentType can't be used because it's related to the lang
  // whereas we want to mix translated contents here
  const localNewsPieceContentType = newsPieceVideo ? 'video' : newsPieceImage ? 'image' : 'text'

  const newsList = listResponse.data

  const userHasReadRights = Boolean(newsPiece?.permissions.self?.includes('read'))
  const rewardsByCheapest = !userHasReadRights
    ? appUtils.subscription.sortSubscriptionsByPrice(newsPiece?.rewards)
    : undefined

  const subscriptionURLBase = `/projects/${project.id}/checkout`
  const subscriptionURLParams = new URLSearchParams()

  if (isClientSide) {
    subscriptionURLParams.set('return_url', encodeURIComponent(location.href))
    if (newsPiece?.rewards) {
      subscriptionURLParams.set('filter', newsPiece.rewards.map((reward) => reward.id).join(','))
    }
  }

  return (
    <>
      <Wrapper>
        <NewsPieceContainer>
          <NavigateBackLink href={`/${project.slug}/news/`} label={t('Back to all posts')} />
          {newsPiece && (
            <>
              <NewsPieceArticle>
                {isEditMode && (
                  <EditButtonWrapper href={`/projects/${project.id}/backoffice/news/${newsPiece.id}/`}>
                    <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
                  </EditButtonWrapper>
                )}
                {localNewsPieceContentType === 'video' && newsPieceVideo?.html && (
                  <MediaWrapper>
                    <VideoLoader
                      isLocked={!userHasReadRights}
                      videoHtml={newsPieceVideo?.html ?? ''}
                      consentWall={consent.media.Wall}
                      placeholder={newsPieceImage?.versions?.['large'].url ?? newsPieceVideo?.thumbnail_url ?? ''}
                    />
                    <plume.styles.card.news.LockLabel asDesktop="L" isLocked={!userHasReadRights} type="video">
                      <plume.glyphs.stroke.Lock size={21} />
                      {t('Locked')}
                    </plume.styles.card.news.LockLabel>
                  </MediaWrapper>
                )}
                {localNewsPieceContentType === 'image' && newsPieceImage && (
                  <MediaWrapper>
                    <plume.styles.card.news.LockLabel asDesktop="L" isLocked={!userHasReadRights} type="image">
                      <plume.glyphs.stroke.Lock size={21} />
                      {t('Locked')}
                    </plume.styles.card.news.LockLabel>
                    <Picture
                      alt={newsPieceTitle}
                      isLocked={!userHasReadRights}
                      src={newsPieceImage?.versions?.['full'].url ?? ''}
                    />
                  </MediaWrapper>
                )}
                <NewsHeader>
                  <plume.styles.heading.M asDesktop="L" as="h1">
                    {newsPieceTitle}
                  </plume.styles.heading.M>
                  <plume.styles.card.news.NewsCardDate>
                    {newsPiece.status === api.NewsStatus.ONLINE
                      ? date.format(date.parseISO(newsPiece.date_publication ?? newsPiece.date_creation), 'PPP')
                      : t('Draft (only visible by you)')}
                  </plume.styles.card.news.NewsCardDate>
                </NewsHeader>

                {newsPiecePreview && (
                  <NewsContent
                    dangerouslySetInnerHTML={{ __html: newsPieceContent ?? newsPiecePreview }}
                    isLocked={!userHasReadRights}
                  />
                )}

                {!userHasReadRights && (
                  <LockOverlay>
                    <plume.styles.copy.M>
                      <plume.glyphs.stroke.Lock size={16} />
                      {t('Post for paying members only')}
                    </plume.styles.copy.M>
                    <plume.RoundedButton
                      onClick={() => track(`${subscriptionURLBase}?${subscriptionURLParams.toString()}`)}
                    >
                      {tc('Subscribe from [amount] / month', {
                        amount: (
                          <plume.LocaleMoneyDisplayer
                            amount={
                              rewardsByCheapest && rewardsByCheapest.length > 0 ? rewardsByCheapest[0].price : null
                            }
                            currency={project.currency}
                            localeFormat={currentLocale}
                            rates={UFE.CURRENCIES.RATES}
                            targetCurrency={project.currency}
                          />
                        ),
                      })}
                    </plume.RoundedButton>
                  </LockOverlay>
                )}
                <plume.styles.card.news.NewsCardFooter>
                  <plume.styles.card.news.CommentsContainer>
                    <BubbleIcon size={16} />
                    <plume.styles.card.news.CommentsCount as={plume.styles.copy.S}>
                      {tnc('%(count)s comment', '%(count)s comments', newsPiece.comments_count, {
                        count: newsPiece.comments_count,
                      })}
                    </plume.styles.card.news.CommentsCount>
                  </plume.styles.card.news.CommentsContainer>
                  {/* FIXME: Rename to `ProjectButton` */}
                  <plume.RoundButton
                    contents={{
                      inactive: (
                        <>
                          <span>{t('Share')}</span>
                          <plume.glyphs.stroke.Share size={17} />
                        </>
                      ),
                      active: (
                        <>
                          <span>{t('Share')}</span>
                          <plume.glyphs.stroke.Share size={17} />
                        </>
                      ),
                    }}
                    onClick={() => setIsProjectDetailShareModalOpen(true)}
                  />
                </plume.styles.card.news.NewsCardFooter>
              </NewsPieceArticle>
              {/* TODO: Lazy? */}
              {newsPiece.status === 'online' && userHasReadRights && (
                <CommentBox>
                  <duvet.comment.CommentBox isCommentingAllowed objectId={newsId} />
                </CommentBox>
              )}
            </>
          )}
        </NewsPieceContainer>
        {newsList?.news && newsList.news.length > 1 && (
          <NewsSiblings>
            <plume.styles.heading.S>
              {tn('Recent post', 'Recent posts', newsList.news.length - 1)}
            </plume.styles.heading.S>
            {newsList?.news
              ?.filter((siblingNewsPiece) => siblingNewsPiece.id !== newsPiece?.id)
              .map((siblingNewsPiece) => {
                const userHasSiblingReadRights = Boolean(siblingNewsPiece.permissions.self?.includes('read'))

                const siblingNewsPieceTitle = models.i18n.getWithLang(
                  siblingNewsPiece.title,
                  currentLocale,
                  project.lang,
                )
                const siblingNewsPieceVideo =
                  siblingNewsPiece.video && models.i18n.getWithLang(siblingNewsPiece.video, currentLocale, project.lang)
                const siblingNewsPieceImage =
                  siblingNewsPiece.image && models.i18n.getWithLang(siblingNewsPiece.image, currentLocale, project.lang)

                // news.contentType can't be used because it's related to the lang
                // whereas we want to mix translated contents here
                const localSiblingNewsPieceContentType = newsPieceVideo ? 'video' : newsPieceImage ? 'image' : 'text'

                return (
                  <NextLink
                    href={`/${project.slug}/news/${siblingNewsPiece.slug}-${siblingNewsPiece.id}/`}
                    key={siblingNewsPiece.id}
                    legacyBehavior
                    passHref
                  >
                    <NewsMiniCard>
                      <NewsMiniCardDefaultImage
                        isLockedWithAsset={!userHasSiblingReadRights && localSiblingNewsPieceContentType !== 'text'}
                      >
                        {!userHasSiblingReadRights && <plume.glyphs.stroke.Lock size={16} />}
                        {userHasSiblingReadRights && localSiblingNewsPieceContentType === 'text' && (
                          <plume.glyphs.stroke.Article size={16} />
                        )}
                        {localSiblingNewsPieceContentType !== 'text' && (
                          <MiniImage
                            alt={siblingNewsPieceTitle}
                            src={siblingNewsPieceImage?.versions?.['small'].url ?? siblingNewsPieceVideo?.thumbnail_url}
                          />
                        )}
                      </NewsMiniCardDefaultImage>
                      <NewsMiniCardWrapper>
                        <NewsMiniCardTitle>{siblingNewsPieceTitle}</NewsMiniCardTitle>
                        <plume.styles.card.news.NewsCardDate>
                          {siblingNewsPiece.status === api.NewsStatus.ONLINE
                            ? date.format(
                                date.parseISO(siblingNewsPiece.date_publication ?? siblingNewsPiece.date_creation),
                                'PPP',
                              )
                            : t('Draft')}
                        </plume.styles.card.news.NewsCardDate>
                      </NewsMiniCardWrapper>
                    </NewsMiniCard>
                  </NextLink>
                )
              })}
            <ViewMoreCTA>
              <NextLink href={`/${project.slug}/news/`} legacyBehavior passHref>
                <plume.Link tinted withIcon>
                  {t('See all posts')}
                  <plume.glyphs.stroke.ArrowRight size={16} />
                </plume.Link>
              </NextLink>
            </ViewMoreCTA>
          </NewsSiblings>
        )}
        {modalContainer &&
          ReactDOM.createPortal(
            <layout.modals.ShareProject
              enableWidget={false}
              project={project}
              sharingContext="news_article"
              trackingConfig={{
                category: CATEGORY.MEMBERSHIP_NEWS,
                source: 'news_article',
              }}
            />,
            modalContainer,
          )}
      </Wrapper>
    </>
  )

  function track(href: string): void {
    tracking.track({
      event: EVENT_TYPE.GENERIC,
      eventCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventAction: 'subscribe',
      eventLabel: newsPieceTitle,
      siteCategory: SITE.ULULE,
      pageCategory: CATEGORY.MEMBERSHIP_NEWS,
      eventCallback: () => {
        if (typeof window !== 'undefined') {
          window.location.href = href
        }
      },
    })
  }
}

const VideoLoader = styled(plume.VideoLoader)`
  flex: none;
  width: 100%;
  height: fit-content;
  aspect-ratio: 16/9;
  border-radius: 4px;
  overflow: hidden;
`

const MediaWrapper = styled.div`
  position: relative;
`

const Picture = styled(plume.Picture)<{ isLocked: boolean }>`
  flex: none;
  border-radius: 4px;
  display: flex;

  ${(props) =>
    props.isLocked &&
    css`
      img {
        filter: blur(16px); // HACK - blur should be applied server side
      }

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${plume.COLORS.PRIMARY_GREY_900};
        opacity: 0.5;
      }
    `}
`

const MiniImage = styled.img`
  position: absolute;
  z-index: 0;

  height: 48px;
  object-fit: cover;
  width: 48px;
  border-radius: 4px;
`

const BubbleIcon = styled(plume.glyphs.stroke.Bubble)`
  margin-top: 2px;
`

const CommentBox = styled.section`
  padding: 16px 0 0;
  margin-bottom: -12px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
    border-radius: 4px;
    box-shadow: ${plume.EFFECTS.SHADOW_5};
    padding: 32px;
    margin-bottom: 0;
  }
`

const EditButtonWrapper = styled.a`
  position: absolute;
  right: 0;
  top: 0;
`

const LockOverlay = styled.div`
  align-content: center;
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;

  ${plume.styles.copy.M} {
    font-weight: 600; // HACK: Irregular font manipulation. We'll be adding copy.M.semiBold, so this is just by anticipation.
    text-align: center;

    ${plume.glyphs.Icon} {
      color: ${plume.COLORS.PRIMARY_SAND_500};
      margin-bottom: 4px;
      margin-right: 6px;
    }
  }
`

const NewsContent = styled(plume.WysiwygContent)<{ isLocked?: boolean }>`
  overflow: hidden;
  position: relative;

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ${({ isLocked = false }) =>
    isLocked &&
    css`
      -webkit-line-clamp: 5;
      max-height: 110px;

      &::after {
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
        content: '';
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }
    `};
`

const NewsHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  word-wrap: break-word;
`

const NewsMiniCard = styled.a`
  display: flex;
  gap: 16px;
  text-decoration: none;
`

const NewsMiniCardDefaultImage = styled.div<{ isLockedWithAsset: boolean }>`
  align-items: center;
  border-radius: 4px;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  width: 48px;
  overflow: hidden;
  position: relative;

  ${plume.glyphs.stroke.Lock} {
    z-index: 10;
  }

  ${({ theme }) => {
    return css`
      background-color: ${theme?.colors?.background ?? plume.COLORS.hexToRgb(plume.COLORS.BRAND_INDIGO, 0.1)};
    `
  }};

  ${plume.glyphs.Icon} {
    color: ${({ isLockedWithAsset, theme }) =>
      isLockedWithAsset ? plume.COLORS.PRIMARY_GREY_000 : theme?.colors?.primary ?? plume.COLORS.BRAND_INDIGO};
  }
`

const NewsMiniCardTitle = styled(plume.styles.heading.XS)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
`

const NewsMiniCardWrapper = styled.div`
  margin-top: -4px;
  word-break: break-word;
`

const NewsPieceArticle = styled.article`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  padding: 16px;
  position: relative;

  ${plume.EditButton} {
    position: absolute;
    right: 16px;
    top: 16px;
    z-index: 2;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 32px;
  }
`

const NewsPieceContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    gap: 24px;
    margin-bottom: 90px;
    width: 70%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    width: 674px;
  }
`

const NewsSiblings = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 auto;
  position: relative;
  top: 6px;
  width: 100%;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 32px 0;
    margin: 0;
    width: 30%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 32px;
    width: 352px;
  }
`

const ViewMoreCTA = styled(plume.styles.copy.S)`
  align-self: end;

  ${plume.Link} {
    ${plume.glyphs.stroke.ArrowRight} {
      color: inherit;
      margin-left: 8px;
      margin-top: 2px;
    }
  }
`

const Wrapper = styled(newsStyle.Wrapper)`
  flex-direction: column;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-direction: row;
    margin-right: 10px;
    margin-left: 10px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    margin-left: auto;
    margin-right: auto;
  }
`
